import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { FormControl, Select, MenuItem, FormHelperText, InputLabel, Typography, Box, Tooltip, Chip } from '@mui/material';

RHFSelectField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array
};

export default function RHFSelectField({ name, options, variant, label, width = 150, noOptionsText = 'No options available', maxDescriptionLength = 100, placeholder, ...other }) {
  const { control } = useFormContext();
  const noOptionsAvailable = options.length === 0;
  const uniqueId = (new Date()).getTime();

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl variant={variant || "outlined"} error={!!error} sx={{ minWidth: width }} fullWidth>
          <InputLabel id={`mui-select-${uniqueId}`}>{label}</InputLabel>
          <Select
            {...field}
            labelId={`mui-select-${uniqueId}`}
            value={typeof field.value === 'number' && field.value === 0 ? '' : field.value}
            displayEmpty={!!placeholder || other.displayEmpty}
            renderValue={(selected) => {
              // Empty state: show the placeholder in muted gray (only when the
              // caller supplied one — otherwise keep the original empty render).
              const isEmpty =
                selected === undefined || selected === null || selected === '';
              if (isEmpty) {
                return placeholder
                  ? (
                    <Typography
                      sx={{
                        color: '#9ca3af',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {placeholder}
                    </Typography>
                  )
                  : '';
              }
              const selectedOption = options.find(option => option.value === selected);
              return (
                <Typography
                  sx={{
                    color: '#1e293b',
                    fontWeight: 500,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {selectedOption?.label || ''}
                </Typography>
              );
            }}
            {...other}
          >
            {noOptionsAvailable ? (
              <MenuItem disabled value="">
                <em>{noOptionsText}</em>
              </MenuItem>
            ) : (
              options.map((e, i) => (
                <MenuItem key={i} value={e.value}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body1">{e.label}</Typography>
                      {e.tag && <Chip label={e.tag} size="small" color={e.tagColor || "default"} />}
                    </Box>
                    {e.description && (
                      <Tooltip title={e.description} placement="right" arrow>
                        <Typography variant="body2" color="textSecondary">
                          {truncateText(e.description, maxDescriptionLength)}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>{error?.message}</FormHelperText>
        </FormControl>
      )}
    />
  );
}
